import { action, computed } from 'mobx';

import api from 'core/util/api';
import { modelFinder } from 'app/stores/util/modelFinder';
import { ModelSaveOptions } from 'core/model/Model';
import { SiteProps } from '@kentik/ui-shared/types/sites';
import { SelectedValueButtonOption } from 'app/components/device/types';
import SiteModel from './SiteModel';
import SiteCollection from './SiteCollection';

export class SiteStore {
  collection = new SiteCollection();

  history?: string[]; // actually react-router history object

  initialize() {
    setTimeout(() => this.collection.fetch(), 0);
  }

  navigateToSite = (siteId: string) => {
    this.history?.push(`/v4/core/quick-views/sites/${siteId}`);
  };

  createSite = (siteAttributes: Partial<SiteProps>, options: ModelSaveOptions) => {
    const site = this.collection.build(undefined, { select: true });
    return site.save(siteAttributes, options);
  };

  /**
   * This is only private to make test mocks easier.
   */
  @computed
  get _byName() {
    return modelFinder<SiteProps, SiteModel, SiteCollection, 'title'>(this.collection, 'title');
  }

  byName(name: string) {
    return this._byName(name);
  }

  @action
  fetchSite = (siteId: string) => {
    const site = new SiteModel({ id: siteId });

    return site.fetch().then(() => site);
  };

  @computed
  get noSitesConfigured() {
    return this.collection.unfilteredSize === 0;
  }

  @computed
  get numSites() {
    return this.collection.unfilteredSize;
  }

  getSiteOption = (
    model: SiteModel
  ): SelectedValueButtonOption & {
    id: string;
    value: string;
    numDevices: number | undefined;
    hasCloudDevices: boolean | undefined;
  } => ({
    id: model.id,
    value: model.id,
    label: model.get('title'),
    activeDevices: model.get('active_device_count'),
    numDevices: model.get('device_count'),
    hasCloudDevices: model.hasCloudDevices
  });

  @computed
  get siteOptions() {
    if (!this.collection.hasFetched) {
      return [];
    }

    return this.collection.map(this.getSiteOption).sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  @computed
  get siteOptionsMap() {
    const hashMap: Record<string, object> = {};

    this.collection.models.forEach((model) => {
      hashMap[model.id] = this.getSiteOption(model);
    });

    return hashMap;
  }

  // Some customers have a lot of devices. If you only need labels then this map will be much faster
  @computed
  get siteLabelsMap() {
    const hashMap: Record<string, object> = {};

    this.collection.models.forEach((model) => {
      hashMap[model.id] = {
        id: model.id,
        value: model.id,
        label: model.get('title')
      };
    });

    return hashMap;
  }

  @computed
  get siteTitleOptions() {
    if (!this.collection.hasFetched) {
      return [];
    }

    return this.collection.map((site) => ({
      id: site.id,
      value: site.get('title'),
      label: site.get('title')
    }));
  }

  @computed
  get sitesGeoMap() {
    return this.collection.reduce(
      (acc, model) => {
        const { id, lat, lon, city, country, region } = model.get();

        acc[id] = {
          city: {
            latitude: lat,
            longitude: lon,
            name: city
          },
          country: {
            code: country,
            name: model.countryName
          },
          region: {
            name: region
          }
        };

        return acc;
      },
      {} as Record<string, { city: object; country: object; region: object }>
    );
  }

  @computed
  get sitesTitleMap() {
    return this.collection.reduce(
      (acc, model) => {
        const { id, title } = model.get();
        if (title) {
          acc[title] = id;
        }
        return acc;
      },
      {} as Record<string, string>
    );
  }

  exportPdf = (search = '') => {
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    import('app/stores/$exports').then(({ default: $exports }) =>
      $exports.fetchExport({
        path: '/api/ui/export/exportPage',
        type: 'pdf',
        fileName: `export-sites-${date}`,
        exportOptions: { location: `/v4/export/settings/sites${search}` }
      })
    );
  };

  exportCsv = (data: { data: SiteProps[] }) => {
    const path = '/api/ui/sites/csv';
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const fileName = `export-sites-${date}`;
    const type = 'csv';
    const options = { path, fileName, type };

    import('app/stores/$exports').then(({ default: $exports }) => {
      $exports.addLoadingExport(options);
      return api.post(path, { data, rawResponse: true }).then((response) => {
        $exports.clearLoadingExport(options);
        $exports.addPayload(response.text, options);
      });
    });
  };

  /**
   * Must be called when making a change to devices - Device-Site membership is now calculated on the backend.
   */
  refreshSites = async (): Promise<void> =>
    this.collection
      .fetch({ force: true, preserveSelection: true })
      .then(() => {})
      .catch((error) => {
        console.error('Failed to refresh sites after device change', error);
      });
}

const $sites = new SiteStore();

export default $sites;
